"use client"
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Doctor } from '@/types';
import { useOrganization } from '@/contexts/organization-context';
import { Checkbox } from './ui/checkbox';

interface DoctorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDoctorCreated: (doctor: Omit<Doctor, 'id'>) => void;
  onDoctorUpdated: (doctor: Doctor) => void;
  doctorToEdit: Doctor | null;
}

export function DoctorFormModal({ isOpen, onClose, onDoctorCreated, onDoctorUpdated, doctorToEdit }: DoctorFormModalProps) {
  const { organization } = useOrganization();
  const [name, setName] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [phone, setPhone] = useState('');
  const [fee, setFee] = useState<number | ''>('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (doctorToEdit) {
      setName(doctorToEdit.name);
      setSpecialization(doctorToEdit.specialization);
      setPhone(doctorToEdit.phone);
      setFee(doctorToEdit.fee ?? '');
      setIsAvailable(doctorToEdit.is_available);
    } else {
      setName('');
      setSpecialization('');
      setPhone('');
      setFee('');
      setIsAvailable(true);
    }
  }, [doctorToEdit]);

  const handleSubmit = async () => {
    if (!organization) {
        toast({ title: "No organization selected", variant: "destructive" });
        return;
    }
    setSaving(true);
    
    const doctorData = {
        name,
        specialization,
        phone,
        fee: fee === '' ? null : fee,
        is_available: isAvailable,
        created_at: doctorToEdit?.created_at || new Date().toISOString()
    };

    if (doctorToEdit) {
        onDoctorUpdated({ ...doctorToEdit, ...doctorData });
    } else {
        onDoctorCreated({ ...doctorData, organization_id: organization.id });
    }
    
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{doctorToEdit ? 'Edit Doctor' : 'Add New Doctor'}</DialogTitle>
          <DialogDescription>
            {doctorToEdit ? 'Update the details of the doctor.' : 'Fill in the details of the new doctor.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="specialization">Specialization</Label>
            <Input id="specialization" value={specialization} onChange={(e) => setSpecialization(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
           <div className="grid gap-2">
            <Label htmlFor="fee">Fee</Label>
            <Input id="fee" type="number" value={fee} onChange={(e) => setFee(parseFloat(e.target.value) || '')} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="isAvailable" checked={isAvailable} onCheckedChange={(checked) => setIsAvailable(!!checked)} />
            <Label htmlFor="isAvailable">Is Available</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 